import { Link, Typography } from 'omakase';
import { consumer, core, useAppContext } from '@tock/coretypes';
import { ensureHttpPrefix } from '@tock/shared/utils/urlUtils';
import { isEmpty, isNil, isUndefined } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';
import { shortenUrlToHost } from 'utils/pathUtils';
import { useAppSettings } from 'selectors/settingsSelector';
import BusinessMapWithScript from 'components/business/landing-page/BusinessMapWithScript';
import DisplayAddress from '@tock/shared/components/DisplayAddress';
import FaqLink from 'components/business/landing-page/FaqLink';
import Hours from 'components/business/landing-page/Hours';
import ParkingInformation from './ParkingInformation';
import PrivateDiningRequest from 'components/business/private-dining/PrivateDiningRequest';
import ResyCreditBanner from 'components/business/landing-page/ResyCreditBanner';
import SocialLinks from 'components/business/landing-page/SocialLinks';
import googleMaps from '@tock/shared/utils/googleMaps';

type MetadataProps = Omit<consumer.BusinessConfig, 'address' | 'services'> & {
  address?: consumer.ILocation;
  isAtBusinessLocation?: boolean;
  services?: core.IService[];
};

const MetadataRow: React.FunctionComponent = ({ children }) => (
  <div className="ProfileMetadata-row">{children}</div>
);

function Metadata({
  address,
  customerFacingPhone,
  domainName,
  email,
  facebookUrl,
  googlePlacesId,
  hidePublicHours,
  instagramId,
  isAtBusinessLocation = true,
  isResyCreditEligible,
  menuUrl,
  name,
  parkingOption = [],
  phoneExtension,
  privateDiningRequestEnabled,
  services,
  shouldDisplayAddress,
  twitterHandle,
  webUrl,
}: MetadataProps) {
  const appContext = useAppContext();
  const settings = useAppSettings();
  const addressString = googleMaps.getAddressString(
    oc(address).address(''),
    oc(address).city(''),
    oc(address).state(''),
    oc(address).country(''),
    oc(address).zipCode('')
  );

  const isMapsEnabled = appContext.context.isFeatureEnabled('GOOGLE_MAPS_ON_BUSINESS_HOMEPAGE');
  const shouldDisplayMap =
    isMapsEnabled && shouldDisplayAddress && !isUndefined(address) && !isEmpty(address.address);

  let metadataTitle = '';

  if (!isNil(services) && !isEmpty(services) && shouldDisplayMap) {
    metadataTitle = 'Hours and location';
  } else if (!isNil(services) && !isEmpty(services) && customerFacingPhone) {
    metadataTitle = 'Hours and information';
  } else {
    metadataTitle = 'More information';
  }

  const phoneHref =
    customerFacingPhone &&
    (phoneExtension
      ? `tel:${customerFacingPhone},${phoneExtension}`
      : `tel:${customerFacingPhone}`);

  const shareLink = `${settings.CONSUMER_URL_BASE}/${domainName}`;

  return (
    <>
      {privateDiningRequestEnabled && <PrivateDiningRequest domainName={domainName} />}
      {isResyCreditEligible && <ResyCreditBanner includeDivider />}
      <h2>
        <Typography component="span" variant="heading02">
          {metadataTitle}
        </Typography>
      </h2>
      <div className="ProfileMeta-columnWrapper">
        {shouldDisplayMap && (
          <BusinessMapWithScript
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            addressFields={address!}
            businessDomainName={domainName}
            businessName={name}
            isAtBusinessLocation={isAtBusinessLocation}
            googlePlacesId={isAtBusinessLocation ? googlePlacesId : undefined}
            lazy
            analyticsEventCategory="Business Map"
          />
        )}
        <div className="ProfileMetadata-content">
          <Typography color="inherit" component="span" variant="body02">
            {shouldDisplayAddress && !isUndefined(address) && (
              <div className="ProfileMetadata-row">
                <Link href={`https://www.google.com/maps/place/${addressString}`} target="_blank">
                  <DisplayAddress location={address} includeBreaks />
                </Link>
              </div>
            )}
            {parkingOption.length > 0 && (
              <MetadataRow>
                <ParkingInformation parkingOptionsOffered={parkingOption} />
              </MetadataRow>
            )}
            {customerFacingPhone && (
              <MetadataRow>
                <Link href={phoneHref}>
                  {customerFacingPhone}
                  {phoneExtension && `, ext. ${phoneExtension}`}
                </Link>
              </MetadataRow>
            )}
            {email && (
              <MetadataRow>
                <Link href={`mailto:${email}`}>Email {name}</Link>
              </MetadataRow>
            )}
            {webUrl && (
              <MetadataRow>
                <Link target="_blank" href={ensureHttpPrefix(webUrl)}>
                  {shortenUrlToHost(webUrl)}
                </Link>
              </MetadataRow>
            )}
            {menuUrl && (
              <MetadataRow>
                <span>
                  <Link target="_blank" href={ensureHttpPrefix(menuUrl)}>
                    View menu
                  </Link>
                </span>
              </MetadataRow>
            )}
            <FaqLink wrapper={MetadataRow}>Frequently asked questions</FaqLink>
            {!isNil(services) && !isEmpty(services) && !hidePublicHours && (
              <MetadataRow>
                <Hours services={services} />
              </MetadataRow>
            )}
          </Typography>
          <SocialLinks
            name={name}
            facebookUrl={facebookUrl}
            instagramId={instagramId}
            twitterHandle={twitterHandle}
            shareLink={shareLink}
          />
        </div>
      </div>
    </>
  );
}

export default Metadata;
